import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Pencil, Save, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTestCase, useUpdateTestCase } from '../../hooks/useTestCases';
import { testRunKeys } from '../../hooks/useTestRuns';
import { useQueryClient } from '@tanstack/react-query';

// --- Constants ---

const TYPE_OPTIONS = [
  { value: 'POSITIVE', label: 'Positive' },
  { value: 'NEGATIVE', label: 'Negative' },
  { value: 'BOUNDARY', label: 'Boundary' },
  { value: 'ALTERNATIVE', label: 'Alternative' },
] as const;

const PRIORITY_OPTIONS = [
  { value: 'BLOCKER', label: '블로커' },
  { value: 'CRITICAL', label: '긴급' },
  { value: 'HIGH', label: '높음' },
  { value: 'MEDIUM', label: '보통' },
  { value: 'LOW', label: '낮음' },
] as const;

const TYPE_LABEL: Record<string, string> = {
  POSITIVE: 'Positive',
  NEGATIVE: 'Negative',
  BOUNDARY: 'Boundary',
  ALTERNATIVE: 'Alternative',
};

const PRIORITY_LABEL: Record<string, string> = {
  BLOCKER: '블로커',
  CRITICAL: '긴급',
  HIGH: '높음',
  MEDIUM: '보통',
  LOW: '낮음',
};

// --- Types ---

interface Step {
  step: number;
  action: string;
}

interface Props {
  projectId: string;
  testCaseId: string;
  runId?: string;
  /** 현재 테스트 결과 상태 (NOT_RUN, PASS, FAIL 등) — 판정 완료 여부 판단용 */
  currentResultStatus?: string;
  onClose: () => void;
}

// --- Component ---

export default function TCDetailSidePanel({ projectId, testCaseId, runId, currentResultStatus, onClose }: Props) {
  const queryClient = useQueryClient();
  const { data: testCase, isLoading } = useTestCase(projectId, testCaseId);
  const updateMutation = useUpdateTestCase(projectId);
  const panelRef = useRef<HTMLDivElement>(null);

  const [isEditing, setIsEditing] = useState(false);

  // Edit form state
  const [title, setTitle] = useState('');
  const [type, setType] = useState('POSITIVE');
  const [priority, setPriority] = useState('MEDIUM');
  const [precondition, setPrecondition] = useState('');
  const [steps, setSteps] = useState<Step[]>([{ step: 1, action: '' }]);
  const [expectedResult, setExpectedResult] = useState('');
  const stepInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Sync form state with fetched testCase
  useEffect(() => {
    if (!testCase) return;
    setTitle(testCase.title);
    setType(testCase.type);
    setPriority(testCase.priority);
    setPrecondition(testCase.precondition ?? '');
    setSteps(
      testCase.steps?.length
        ? testCase.steps.map((s) => ({ step: s.step, action: s.action }))
        : [{ step: 1, action: '' }],
    );
    setExpectedResult(testCase.expectedResult ?? '');
    setIsEditing(false);
  }, [testCase]);

  // ESC key handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // --- Step management ---
  const addStep = () => {
    setSteps((prev) => [...prev, { step: prev.length + 1, action: '' }]);
  };

  const removeStep = (index: number) => {
    if (steps.length <= 1) return;
    setSteps((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.map((s, i) => ({ ...s, step: i + 1 }));
    });
  };

  const updateStep = (index: number, value: string) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, action: value } : s)));
  };

  const handleStepKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const nextIdx = idx + 1;
    if (nextIdx < steps.length && !steps[nextIdx].action.trim()) {
      stepInputRefs.current[nextIdx]?.focus();
    } else {
      setSteps((prev) => [...prev, { step: prev.length + 1, action: '' }]);
      requestAnimationFrame(() => {
        stepInputRefs.current[nextIdx]?.focus();
      });
    }
  };

  // --- Cancel with confirmation ---
  const handleCancel = useCallback(() => {
    if (!testCase) return;
    const hasChanges =
      title !== testCase.title ||
      type !== testCase.type ||
      priority !== testCase.priority ||
      precondition !== (testCase.precondition ?? '') ||
      expectedResult !== (testCase.expectedResult ?? '') ||
      JSON.stringify(steps.map((s) => s.action)) !==
        JSON.stringify((testCase.steps ?? []).map((s) => s.action));

    if (hasChanges) {
      if (!window.confirm('수정 내용을 저장하지 않고 취소하시겠습니까?')) return;
    }

    setTitle(testCase.title);
    setType(testCase.type);
    setPriority(testCase.priority);
    setPrecondition(testCase.precondition ?? '');
    setSteps(
      testCase.steps?.length
        ? testCase.steps.map((s) => ({ step: s.step, action: s.action }))
        : [{ step: 1, action: '' }],
    );
    setExpectedResult(testCase.expectedResult ?? '');
    setIsEditing(false);
  }, [testCase, title, type, priority, precondition, expectedResult, steps]);

  // --- Save ---
  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('제목을 입력해주세요.');
      return;
    }

    const isJudged = currentResultStatus && currentResultStatus !== 'NOT_RUN';
    const originalSteps = testCase?.steps ?? [];
    const newSteps = steps.map((s) => ({ step: s.step, action: s.action.trim() }));

    // 스텝 삭제 시 기존 판정 결과 유실 경고
    if (isJudged && newSteps.length < originalSteps.length) {
      const removedCount = originalSteps.length - newSteps.length;
      const confirmed = window.confirm(
        `${removedCount}개의 테스트 단계가 삭제됩니다. 이미 판정된 결과가 있는 경우 재테스트가 필요합니다.\n계속하시겠습니까?`,
      );
      if (!confirmed) return;
    }

    // 판정된 스텝의 내용이 변경되었는지 확인
    const stepsModified =
      isJudged &&
      originalSteps.some((orig, i) => {
        const updated = newSteps[i];
        return updated && orig.action !== updated.action;
      });

    try {
      await updateMutation.mutateAsync({
        testCaseId,
        data: {
          title: title.trim(),
          type: type as 'POSITIVE' | 'NEGATIVE' | 'BOUNDARY' | 'ALTERNATIVE',
          priority: priority as 'BLOCKER' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW',
          precondition: precondition.trim() || null,
          steps: newSteps,
          expectedResult: expectedResult.trim() || null,
        },
      });
      // 테스트 실행 결과도 갱신 (REQ-V44-007: TC 수정 시 실행 화면에 반영)
      if (runId) {
        queryClient.invalidateQueries({ queryKey: testRunKeys.results(projectId, runId) });
      }
      toast.success('테스트케이스가 수정되었습니다.');

      // 이미 판정된 스텝이 수정된 경우 안내 토스트 (REQ-V44-007)
      if (stepsModified) {
        toast('이미 판정된 스텝의 내용이 수정되었습니다. 필요시 재테스트해주세요.', { icon: '⚠️', duration: 5000 });
      }

      setIsEditing(false);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || '수정에 실패했습니다.');
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Side Panel */}
      <div
        ref={panelRef}
        className="fixed inset-y-0 right-0 w-[460px] bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col animate-slide-in-right"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">테스트케이스 상세</h3>
            {testCase && (
              <span className="font-mono text-sm text-gray-500 shrink-0">{testCase.tcId}</span>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                disabled={isLoading || !testCase}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50"
              >
                <Pencil className="size-3" />
                편집
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="px-2.5 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  취소
                </button>
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  <Save className="size-3" />
                  {updateMutation.isPending ? '저장 중...' : '저장'}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="size-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent" />
            </div>
          ) : !testCase ? (
            <div className="text-center text-sm text-gray-400 py-12">
              테스트케이스를 찾을 수 없습니다.
            </div>
          ) : isEditing ? (
            /* ─── Edit Mode ─── */
            <>
              {/* 제목 */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">제목</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </div>

              {/* 유형 + 우선순위 */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">유형</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">우선순위</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                  >
                    {PRIORITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 사전조건 */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">사전조건</label>
                <textarea
                  value={precondition}
                  onChange={(e) => setPrecondition(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                />
              </div>

              {/* 테스트 단계 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-500">테스트 단계</label>
                  <button
                    onClick={addStep}
                    className="inline-flex items-center gap-1 px-2 py-1 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                  >
                    <Plus className="size-3" />
                    추가
                  </button>
                </div>
                <div className="space-y-2">
                  {steps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-2 bg-gray-50 rounded-lg p-2.5">
                      <span className="mt-2 text-sm font-mono text-gray-400 w-5 shrink-0 text-center">
                        {step.step}
                      </span>
                      <input
                        ref={(el) => { stepInputRefs.current[idx] = el; }}
                        type="text"
                        value={step.action}
                        onChange={(e) => updateStep(idx, e.target.value)}
                        onKeyDown={(e) => handleStepKeyDown(e, idx)}
                        placeholder="동작 (Action)"
                        className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                      />
                      <button
                        onClick={() => removeStep(idx)}
                        disabled={steps.length <= 1}
                        className="mt-1.5 p-1 rounded hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="size-3.5 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 기대 결과 */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">기대 결과</label>
                <textarea
                  value={expectedResult}
                  onChange={(e) => setExpectedResult(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                />
              </div>
            </>
          ) : (
            /* ─── Read Mode ─── */
            <>
              {/* Meta info */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-0.5">TC ID</span>
                  <span className="text-sm font-mono text-gray-900">{testCase.tcId}</span>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-0.5">유형</span>
                  <span className="text-sm text-gray-900">{TYPE_LABEL[testCase.type] ?? testCase.type}</span>
                </div>
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-0.5">우선순위</span>
                  <span className="text-sm text-gray-900">{PRIORITY_LABEL[testCase.priority] ?? testCase.priority}</span>
                </div>
              </div>

              {testCase.specification && (
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-0.5">연결 명세</span>
                  <span className="text-sm text-gray-900">
                    {testCase.specification.functionCode} {testCase.specification.title}
                  </span>
                </div>
              )}

              {/* 제목 */}
              <div>
                <span className="block text-sm font-medium text-gray-500 mb-0.5">제목</span>
                <p className="text-sm text-gray-900">{testCase.title}</p>
              </div>

              {/* 사전조건 */}
              {testCase.precondition && (
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-0.5">사전조건</span>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{testCase.precondition}</p>
                </div>
              )}

              {/* 테스트 단계 */}
              {testCase.steps && testCase.steps.length > 0 && (
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-1.5">테스트 단계</span>
                  <div className="space-y-1.5">
                    {testCase.steps.map((step, idx) => (
                      <div key={idx} className="flex gap-2 bg-gray-50 rounded-lg p-2.5">
                        <span className="text-sm font-mono text-gray-400 w-5 shrink-0 text-center mt-0.5">
                          {step.step}
                        </span>
                        <p className="flex-1 text-sm text-gray-900">{step.action}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 기대 결과 */}
              {testCase.expectedResult && (
                <div>
                  <span className="block text-sm font-medium text-gray-500 mb-0.5">기대 결과</span>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{testCase.expectedResult}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
